// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

/**
 * General conditions, built the way a GC actually builds them: staff × time.
 *
 * Division 01 is routinely 8-15% of a job and it is almost never a quantity
 * takeoff. It is a superintendent for the duration, a project manager for
 * most of it, a trailer, a dumpster rotation. The number that drives it is
 * the schedule, and this app already had all three inputs -- labor rates on
 * the org, a duration on the project, a Division 01 to put it in -- sitting
 * in three places that did not talk to each other. A four-month extension
 * moved the schedule and left the estimate alone.
 *
 * What this does NOT do is drive line items silently. The estimate's quantities
 * are commitments, not projections: the same reason logistics quantities are
 * deliberately unlinked from drawn areas. So the staffing plan WRITES line
 * items when asked, and afterwards says when the schedule has moved out from
 * under them. The user re-runs it or does not; nothing changes behind them.
 *
 * The lines it writes are ordinary line items -- qty in months, unit MO, unit
 * cost per month -- so they read, price, filter, export and get marked up like
 * anything else, and an estimator can edit one without this module involved.
 */

export interface GcStaffRow {
  id: string;
  /** Free text, usually matching an org labor rate. */
  role: string;
  /** Hourly, as the org labor rates are kept. */
  hourlyRate: number;
  /** How long this person is on the job. */
  months: number;
  /** Percent of full time. A PM across two jobs is 50. */
  allocation: number;
}

/** 2080 hours a year over twelve months. The usual convention. */
export const HOURS_PER_MONTH = 2080 / 12;

/** The subcategory this module owns. Re-running replaces only these rows. */
export const GC_SUB_NAME = "Project Staffing";

const num = (v: any) => (Number.isFinite(v) ? v : 0);

/** A full-time month of this role, before allocation. */
export const fullMonthlyCost = (row: GcStaffRow) => num(row?.hourlyRate) * HOURS_PER_MONTH;

/**
 * What this role costs per month at its allocation.
 *
 * Allocation is folded in here rather than carried as a third factor on the
 * line, so the written line item reads "24 MO @ $12,567" and multiplies out
 * on the sheet. A half-time PM costs half a PM per month; that is the honest
 * sentence, and it survives somebody editing the quantity later.
 */
export const monthlyCost = (row: GcStaffRow) =>
  fullMonthlyCost(row) * (num(row?.allocation) / 100);

export const rowTotal = (row: GcStaffRow) => monthlyCost(row) * num(row?.months);

export const gcTotal = (rows: GcStaffRow[]) =>
  (rows || []).reduce((a, r) => a + rowTotal(r), 0);

const rid = () => `gc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

/**
 * A starting plan, from the org's own rates.
 *
 * Full-time superintendent, half-time PM, half-time APM is the shape of a
 * typical single-site job. It is a starting point to argue with, not a
 * recommendation -- which is why every field is editable and the total is
 * shown before anything is written.
 */
export function defaultStaff(
  durationMonths: number,
  rates: Array<{ role: string; rate: number }>,
): GcStaffRow[] {
  // Exact before partial, because "Project Manager" is a substring of
  // "Assistant Project Manager" -- and that list is usually alphabetical, so
  // a plain `includes` hands the PM row the assistant's rate and the estimate
  // is quietly light by the difference.
  const find = (needle: string) => {
    const want = needle.toLowerCase();
    const all = rates || [];
    return all.find(r => r.role.toLowerCase() === want)
      || all.find(r => r.role.toLowerCase().includes(want));
  };
  const months = num(durationMonths) > 0 ? durationMonths : 12;
  const plan: Array<[string, string, number]> = [
    ["Superintendent", "superintendent", 100],
    ["Project Manager", "project manager", 50],
    ["Assistant Project Manager", "assistant project", 50],
  ];
  return plan
    .map(([label, needle, allocation]) => {
      const match = find(needle);
      if (!match) return null;
      return { id: rid(), role: match.role, hourlyRate: match.rate, months, allocation };
    })
    .filter(Boolean) as GcStaffRow[];
}

export const blankRow = (months: number): GcStaffRow =>
  ({ id: rid(), role: "", hourlyRate: 0, months: num(months) || 0, allocation: 100 });

/**
 * Whether the plan still matches the project.
 *
 * Returns the rows whose month count has drifted from the project duration.
 * Used to say so in the estimate rather than to fix it: a staffing decision
 * that deliberately runs short of the schedule is legitimate, and a tool that
 * silently "corrects" it is worse than one that mentions it.
 */
export function staleRows(rows: GcStaffRow[], durationMonths: number): GcStaffRow[] {
  const d = num(durationMonths);
  if (d <= 0) return [];
  return (rows || []).filter(r => Math.abs(num(r.months) - d) > 0.01);
}

/** The line items this plan becomes. */
export function toLineItems(rows: GcStaffRow[]): Array<{
  description: string; qty: number; unit: string; unitCost: number;
}> {
  return (rows || [])
    .filter(r => r.role && num(r.months) > 0 && monthlyCost(r) > 0)
    .map(r => ({
      // The allocation belongs in the name: the rate already has it baked in,
      // and without it the sheet shows a project manager at half price with
      // no explanation on the row.
      description: num(r.allocation) === 100 ? r.role : `${r.role} (${num(r.allocation)}%)`,
      qty: num(r.months),
      unit: "MO",
      unitCost: monthlyCost(r),
    }));
}
